import React, { useState, useEffect, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { RiDeleteBin6Line } from "react-icons/ri";
import { FaDownload, FaTrashAlt, FaFileExcel, FaQrcode } from "react-icons/fa";
import ReactSelect from "react-select";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import {
  useGetAssetByAcademyQuery,
  useUpdateFloorAndRoomsMutation,
} from "../../../slices/assetApiSlice";
import { useGetUserByEmailQuery } from "../../../slices/userApiSlice";
import { toast } from "sonner";
import { useSelector } from "react-redux";
import { createSelector } from "reselect";
import { IoMdAdd } from "react-icons/io";
import { IoIosCloseCircle } from "react-icons/io";
import {
  X,
  Search,
  Download,
  Filter,
  Eye,
  QrCode,
  Upload,
  Plus,
  Trash2,
  Calendar,
  Clock,
  FileSpreadsheet,
  Package,
  FileText,
  MapPin,
  Ruler,
  Hash,
  UploadCloud,
  ChevronDown,
  Building as BuildingIcon,
  Home,
  DoorClosed,
} from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Badge } from "../../../components/ui/badge";
import { Checkbox } from "../../../components/ui/checkbox";
import DataTablePagination from "../../../components/common/DataTablePagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";

const selectUserInfo = (state) => state.auth.userInfo || {};
const getUserEmail = createSelector(
  selectUserInfo,
  (userInfo) => userInfo?.username || "",
);

const RoomQR = () => {
  const email = useSelector(getUserEmail);
  const { data: manager } = useGetUserByEmailQuery(email);
  const [updateFloorAndRooms, { isLoading }] = useUpdateFloorAndRoomsMutation();
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState("");
  const [selectedFloor, setSelectedFloor] = useState("");
  const [selectedRoom, setSelectedRoom] = useState("");
  const [academyId, setAcademyId] = useState(null);
  const { data: assets, refetch } = useGetAssetByAcademyQuery(academyId);
  const [showForm, setShowForm] = useState(false);
  const [newFloor, setNewFloor] = useState("");
  const [newRooms, setNewRooms] = useState("");
  const [data, setData] = useState([]);
  const [dataRoom, setDataRoom] = useState([]);
  const [roomInput, setRoomInput] = useState("");
  const [floorAndRooms, setFloorAndRooms] = useState({});
  const [floorInput, setFloorInput] = useState("");
  const [selectedAssetTitle, setSelectedAssetTitle] = useState("");
  const [selectedAssetCode, setSelectedAssetCode] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const rowsPerPage = 10; // 3x3 grid for QR codes per page
  const qrSize = 40; // Size of each QR code (adjust as needed)
  const qrSpacing = 12; // Spacing between QR codes

  useEffect(() => {
    if (manager?.user?.academyId) {
      setAcademyId(manager.user.academyId);
    }
  }, [manager?.user?.academyId]);

  useEffect(() => {
    if (assets) {
      // Filter to get only buildings with floors and rooms for the table display
      const filteredAssets = assets.filter((asset) => {
        const isBuilding = asset.categoryDetails?.name === "Building";
        const isInUsage = asset.status === "In Usage";
        const isInMaintenance = asset.status === "In Maintenance";

        // Must be a building and either In Usage or In Maintenance
        if (!isBuilding || (!isInUsage && !isInMaintenance)) return false;

        // Check if building has floors and rooms
        const floorRoomsAttr = asset.attributes?.find(
          (attr) => attr.name === "Floor and rooms",
        );
        if (!floorRoomsAttr) return false;

        try {
          const floorRoomObj = JSON.parse(floorRoomsAttr.value);
          // Only include if there are floors with rooms
          return Object.keys(floorRoomObj).length > 0;
        } catch {
          return false;
        }
      });
      setData(filteredAssets);
    }
  }, [assets]);

  useEffect(() => {
    if (assets) {
      // Get ALL buildings for the dropdown (In Usage or In Maintenance)
      const filteredAssets = assets.filter((asset) => {
        const isBuilding = asset.categoryDetails?.name === "Building";
        const isInUsage = asset.status === "In Usage";
        const isInMaintenance = asset.status === "In Maintenance";
        return isBuilding && (isInUsage || isInMaintenance);
      });
      setDataRoom(filteredAssets);
    }
  }, [assets]);

  const styles = {
    textArea: {
      width: "100%",
      height: "120px",
      padding: "10px",
      fontSize: "14px",
      background: "#f8f8f8",
      border: "1px solid #ddd",
      borderRadius: "4px",
      resize: "none",
      overflowY: "auto",
    },
    floorBlock: {
      marginBottom: "10px",
    },
    floorText: {
      fontSize: "14px",
      marginBottom: "4px",
    },
    noRoomText: {
      fontSize: "13px",
      color: "#888",
      fontStyle: "italic",
      marginLeft: "10px",
    },
    emptyMessage: {
      fontStyle: "italic",
      color: "#888",
      textAlign: "center",
    },
  };

  // Sorting and filtering logic
  const [sortOrder, setSortOrder] = useState({ column: "mid", ascending: false });
  const filteredData = [...data]
    .filter((item) => {
      const matchesSearch = Object.values(item).some((value) =>
        value?.toString().toLowerCase().includes(searchTerm.toLowerCase()),
      );

      const matchesBuilding =
        selectedBuilding === "" || item.title === selectedBuilding;

      const floorRoomsAttr = item.attributes.find(
        (attr) => attr.name === "Floor and rooms",
      );
      const floorRoomObj = floorRoomsAttr
        ? JSON.parse(floorRoomsAttr.value)
        : {};
      const floors = Object.keys(floorRoomObj);
      const rooms = Object.values(floorRoomObj).flat();

      const matchesFloor =
        selectedFloor === "" || floors.includes(selectedFloor);
      const matchesRoom = selectedRoom === "" || rooms.includes(selectedRoom);

      return matchesSearch && matchesBuilding && matchesFloor && matchesRoom;
    })
    .sort((a, b) => {
      if (!sortOrder.column) return 0;

      let valA = a[sortOrder.column];
      let valB = b[sortOrder.column];

      if (valA === undefined || valA === null) valA = "";
      if (valB === undefined || valB === null) valB = "";

      if (!isNaN(valA) && !isNaN(valB)) {
        valA = Number(valA);
        valB = Number(valB);
      } else {
        valA = valA.toString().toLowerCase();
        valB = valB.toString().toLowerCase();
      }

      if (valA < valB) return sortOrder.ascending ? -1 : 1;
      if (valA > valB) return sortOrder.ascending ? 1 : -1;
      return 0;
    });

  // Flattened rows memoized
  const allRows = useMemo(() => {
    let indexCounter = 0;
    return filteredData.flatMap((item) => {
      const floorRoomsAttr = item.attributes?.find(
        (attr) => attr.name === "Floor and rooms",
      );
      const floorData = floorRoomsAttr ? JSON.parse(floorRoomsAttr.value) : {};

      return Object.entries(floorData).flatMap(([floor, rooms]) => {
        if (selectedFloor && floor !== selectedFloor) return [];

        return rooms
          .filter((room) => !selectedRoom || room === selectedRoom)
          .map((room) => {
            indexCounter++;
            const rowKey = `${item.assetID}_${floor}_${room}`;
            const qrCodeAttr = item.attributes.find(
              (attr) => attr.name === `QR Code - Room ${room}`,
            );
            const qrCodeUrl = qrCodeAttr?.value || "";

            return {
              rowKey,
              index: indexCounter,
              assetID: item.assetID,
              assetCode: item.assetCode,
              title: item.title,
              floor,
              room,
              qrCodeUrl,
            };
          });
      });
    });
  }, [filteredData, selectedFloor, selectedRoom]);
  const totalPages = Math.ceil(allRows.length / rowsPerPage);

  // Paginated rows
  const displayedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return allRows.slice(start, start + rowsPerPage);
  }, [allRows, currentPage]);

  // Select all handler
  const handleSelectAll = () => {
    if (selectedRows.length === allRows.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(allRows.map((row) => row.rowKey));
    }
  };

  // console.log("filtereeeee: ", filteredData);
  const displayedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  console.log("displayyyy: ", displayedData);

  const uniqueBuilding = [
    { value: "all", label: "All Buildings" },
    ...Array.from(new Set((data || []).map((item) => item.title)))
      .filter(Boolean)
      .map((title) => ({
        value: title,
        label: title,
      })),
  ];

  const uniqueFloors = [
    { value: "all", label: "All Floors" },
    ...Array.from(
      new Set(
        (data || [])
          .filter(
            (item) =>
              selectedBuilding === "" || item.title === selectedBuilding,
          )
          .flatMap((item) => {
            const floorRoomAttr = item.attributes.find(
              (attr) => attr.name === "Floor and rooms",
            );
            const floorRoomObj = floorRoomAttr
              ? JSON.parse(floorRoomAttr.value)
              : {};
            return Object.keys(floorRoomObj);
          }),
      ),
    ).map((floor) => ({
      value: floor,
      label: floor,
    })),
  ];

  const uniqueRoom = [
    { value: "all", label: "All Rooms" },
    ...Array.from(
      new Set(
        (data || [])
          .filter(
            (item) =>
              (selectedBuilding === "" || item.title === selectedBuilding) &&
              (selectedFloor === "" ||
                item.attributes.some(
                  (attr) =>
                    attr.label === "Floor" && attr.value === selectedFloor,
                )),
          )
          .flatMap((item) => {
            const floorRoomAttr = item.attributes.find(
              (attr) => attr.name === "Floor and rooms",
            );
            const floorRoomObj = floorRoomAttr
              ? JSON.parse(floorRoomAttr.value)
              : {};
            return Object.entries(floorRoomObj)
              .filter(
                ([floor]) => selectedFloor === "" || floor === selectedFloor,
              )
              .flatMap(([, rooms]) => rooms);
          }),
      ),
    ).map((room) => ({
      value: room,
      label: `Room ${room}`,
    })),
  ];

  const assetOptions = useMemo(() => {
    return dataRoom.map((asset) => {
      const floorRoomsAttr = asset.attributes?.find(
        (attr) => attr.name === "Floor and rooms",
      );
      return {
        value: asset.assetCode,
        label: asset.title,
        assetCode: asset.assetCode,
        existingFloorRooms: floorRoomsAttr
          ? JSON.parse(floorRoomsAttr.value)
          : {},
      };
    });
  }, [dataRoom]);

  console.log("Asset Options:", assetOptions);
  console.log("dataRoom:", dataRoom);

  const addRoom = () => {
    if (floorInput.trim() && roomInput.trim()) {
      setFloorAndRooms((prev) => {
        const updatedRooms = { ...prev };
        if (!updatedRooms[floorInput]) {
          updatedRooms[floorInput] = [];
        }
        updatedRooms[floorInput].push(roomInput);
        JSON.stringify(updatedRooms, null, 2); // Update the jsonData
        return updatedRooms;
      });
      setRoomInput(""); // Clear room input
    }
  };

  const loadImageAsDataURL = async (url) => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  const handleDownloadPDF = async () => {
    // Determine which rows to download: selected rows or all rows if none selected
    const rowsToDownload =
      selectedRows.length > 0
        ? allRows.filter((row) => selectedRows.includes(row.rowKey))
        : allRows;

    if (rowsToDownload.length === 0) {
      alert("No assets available to download.");
      return;
    }

    const doc = new jsPDF("p", "mm", [100, 150]);

    // Page size: 100mm wide × 150mm tall (matches physical label paper)
    const pageWidth = 100;
    const pageHeight = 150;
    const marginX = 5;
    const marginY = 5;

    // QR code: 80mm square, centered
    const qrCodeSize = 80;
    const centerX = pageWidth / 2;

    for (let i = 0; i < rowsToDownload.length; i++) {
      const row = rowsToDownload[i];
      const qrUrl = row.qrCodeUrl;

      if (!qrUrl) continue;

      // Add a new page for each label (since we're doing 1 per page)
      if (i > 0) {
        doc.addPage([pageWidth, pageHeight]);
      }

      const qrX = (pageWidth - qrCodeSize) / 2;
      const qrY = marginY + 5;

      try {
        const qrDataUrl = await loadImageAsDataURL(qrUrl);

        // Draw border
        doc.setDrawColor(100, 100, 100);
        doc.setLineWidth(0.5);
        doc.rect(
          marginX,
          marginY,
          pageWidth - 2 * marginX,
          pageHeight - 2 * marginY,
        );

        // Add QR code
        doc.addImage(qrDataUrl, "PNG", qrX, qrY, qrCodeSize, qrCodeSize);

        // Add text below QR code
        let currentY = qrY + qrCodeSize + 6;
        const maxTextWidth = pageWidth - 10;

        // Asset title (bold)
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0, 0, 0);
        const titleLines = doc.splitTextToSize(row.title || "", maxTextWidth);
        doc.text(titleLines, centerX, currentY, { align: "center" });
        currentY += titleLines.length * 5;

        // Floor and Room
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(40, 40, 40);
        doc.text(`Floor: ${row.floor} | Room: ${row.room}`, centerX, currentY, {
          align: "center",
        });
        currentY += 5;

        // Category
        doc.text(`Category: Room`, centerX, currentY, { align: "center" });
        currentY += 5;

        // Asset Code
        doc.setFontSize(8);
        doc.setTextColor(80, 80, 80);
        const codeLines = doc.splitTextToSize(row.assetCode, maxTextWidth);
        doc.text(codeLines, centerX, currentY, { align: "center" });
        doc.setTextColor(0, 0, 0);
      } catch (err) {
        console.error("Failed to load QR image:", err);
      }
    }

    doc.save("Room_QR_Codes.pdf");
  };

  const handleDownloadDetail = () => {
    const rowsToDownload =
      selectedRows.length > 0
        ? allRows.filter((row) => selectedRows.includes(row.rowKey))
        : allRows;

    if (rowsToDownload.length === 0) {
      toast.error("No assets available to download.");
      return;
    }

    const exportData = rowsToDownload.map((row) => ({
      "Asset Code": row.assetCode,
      "Building Name": row.title,
      Floor: row.floor,
      Room: row.room,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Rooms");
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(blob, "Room_Details.xlsx");
    toast.success("Details downloaded successfully!");
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one room to delete.");
      return;
    }
    setShowDeleteConfirm(true);
  };

  const confirmBulkDelete = async () => {
    setIsDeleting(true);
    const selectedObj = allRows.filter((row) =>
      selectedRows.includes(row.rowKey),
    );

    // Group deletions by building
    const deletionsByBuilding = {};
    selectedObj.forEach((row) => {
      if (!deletionsByBuilding[row.assetCode]) {
        deletionsByBuilding[row.assetCode] = [];
      }
      deletionsByBuilding[row.assetCode].push({
        floor: row.floor,
        room: row.room,
      });
    });

    try {
      for (const assetCode of Object.keys(deletionsByBuilding)) {
        const buildingAsset = data.find((a) => a.assetCode === assetCode);
        if (!buildingAsset) continue;

        const floorRoomsAttr = buildingAsset.attributes.find(
          (attr) => attr.name === "Floor and rooms",
        );
        if (!floorRoomsAttr) continue;

        const floorRoomObj = JSON.parse(floorRoomsAttr.value);

        // Remove selected rooms
        deletionsByBuilding[assetCode].forEach((del) => {
          if (floorRoomObj[del.floor]) {
            floorRoomObj[del.floor] = floorRoomObj[del.floor].filter(
              (r) => r !== del.room,
            );
            // If floor is empty, remove floor? Actually better to keep it unless the user explicitly wants to delete the floor too.
            // But usually empty floor should be removed for cleanliness.
            if (floorRoomObj[del.floor].length === 0) {
              delete floorRoomObj[del.floor];
            }
          }
        });

        const payload = {
          assetCode: assetCode,
          name: "Floor and rooms",
          value: JSON.stringify(floorRoomObj),
        };

        await updateFloorAndRooms(payload).unwrap();
      }

      toast.success("Rooms deleted successfully!");
      setSelectedRows([]);
      refetch();
    } catch (error) {
      console.error("Error deleting rooms:", error);
      toast.error("Failed to delete some rooms.");
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleCloseModal = () => {
    setShowForm(false);
    setFloorAndRooms({});
    setFloorInput("");
    setRoomInput("");
    setSelectedAssetCode("");
    setSelectedAssetTitle("");
  };

  const handleSelectRow = (assetID, floor, room) => {
    const rowKey = `${assetID}_${floor}_${room}`; // Make sure this is a string
    setSelectedRows((prev) =>
      prev.includes(rowKey)
        ? prev.filter((item) => item !== rowKey)
        : [...prev, rowKey],
    );
  };

  const handleAddFloorRooms = async () => {
    if (!selectedAssetCode) {
      toast.warning("Please select a building first!");
      return;
    }
    // Show confirmation dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmAddFloorRooms = async () => {
    setShowConfirmDialog(false);

    const payload = {
      assetCode: selectedAssetCode,
      name: "Floor and rooms",
      value: JSON.stringify(floorAndRooms),
    };
    console.log(payload);

    toast.promise(updateFloorAndRooms(payload), {
      loading: "Adding floors and rooms...",
      success: () => {
        refetch();
        setFloorAndRooms({});
        setFloorInput("");
        setSelectedAssetCode("");
        setSelectedAssetTitle("");
        setShowForm(false);
        return "Floor and rooms added successfully!";
      },
      error: (error) => {
        console.error("Error adding floor and rooms:", error);
        return "Unable to add floor and rooms at this time. Please try again later.";
      },
    });
  };

  const handleSort = (column) => {
    const newSortOrder =
      column === sortOrder.column ? !sortOrder.ascending : true;

    setSortOrder({
      column,
      ascending: newSortOrder,
    });
  };

  return (
    <div className="managerDashboard">
      {/* Header Section with Search and Add Button */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            type="text"
            placeholder="Search rooms..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex flex-col gap-2 w-full lg:w-auto items-end">
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              onClick={() => setShowForm(true)}
              className="gap-2 h-10 px-4"
              style={{
                backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
              }}
            >
              <Plus className="w-4 h-4" />
              Add Floor and Rooms
            </Button>
          </div>
        </div>
      </div>

      {/* Dropdowns for filtering */}
      <div className="flex flex-wrap gap-3 mb-6">
        <Select
          value={selectedBuilding || "all"}
          onValueChange={(value) => {
            setSelectedBuilding(value === "all" ? "" : value);
            setSelectedFloor(""); // Reset floor selection
            setSelectedRoom(""); // Reset room selection
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Buildings" />
          </SelectTrigger>
          <SelectContent>
            {uniqueBuilding.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedFloor || "all"}
          onValueChange={(value) => {
            setSelectedFloor(value === "all" ? "" : value);
            setSelectedRoom(""); // Reset room selection
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Floors" />
          </SelectTrigger>
          <SelectContent>
            {uniqueFloors.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedRoom || "all"}
          onValueChange={(value) =>
            setSelectedRoom(value === "all" ? "" : value)
          }
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Rooms" />
          </SelectTrigger>
          <SelectContent>
            {uniqueRoom.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {/* Bulk Action Buttons Section - Below Filters (Right Aligned) */}
      <div className="flex flex-col gap-4 mb-6 px-1">
        {selectedRows.length > 0 && (
          <div className="flex flex-col sm:flex-row justify-end items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
            <div className="flex flex-wrap justify-end items-center gap-2">
              <Button
                onClick={handleDownloadDetail}
                variant="outline"
                className="gap-2 shrink-0 bg-secondary text-secondary-foreground hover:bg-secondary/80 border-none h-10 px-4"
              >
                <FileSpreadsheet className="h-4 w-4" />
                Download Detail ({selectedRows.length})
              </Button>
              <Button
                onClick={handleDownloadPDF}
                variant="outline"
                className="gap-2 shrink-0 bg-secondary text-secondary-foreground hover:bg-secondary/80 border-none h-10 px-4"
              >
                <QrCode className="h-4 w-4" />
                Download QR ({selectedRows.length})
              </Button>
              {/* <Button
                onClick={handleBulkDelete}
                variant="destructive"
                className="gap-2 shrink-0 h-10 px-4 shadow-sm"
              >
                <RiDeleteBin6Line className="h-4 w-4" />
                Delete Selected ({selectedRows.length})
              </Button> */}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12 text-center">
                <Checkbox
                  checked={
                    selectedRows.length === allRows.length && allRows.length > 0
                  }
                  onCheckedChange={handleSelectAll}
                />
              </TableHead>
              {/* <TableHead className="font-semibold">Sl. No.</TableHead> */}
              <TableHead className="font-semibold px-4 py-3">
                <div className="flex items-center gap-2">Asset Code</div>
              </TableHead>
              <TableHead className="font-semibold">Building Name</TableHead>
              <TableHead className="font-semibold">Floor</TableHead>
              <TableHead className="font-semibold">Room</TableHead>
              <TableHead className="text-center font-semibold">
                QR Code
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {displayedRows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={7}
                  className="text-center py-12 text-muted-foreground"
                >
                  No rooms found
                </TableCell>
              </TableRow>
            ) : (
              displayedRows.map((row) => (
                <TableRow key={row.rowKey} className="hover:bg-muted/50">
                  <TableCell className="text-center">
                    <Checkbox
                      checked={selectedRows.includes(row.rowKey)}
                      onCheckedChange={() =>
                        handleSelectRow(row.assetID, row.floor, row.room)
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">{row.assetCode}</Badge>
                  </TableCell>
                  <TableCell className="font-medium">{row.title}</TableCell>
                  <TableCell>{row.floor}</TableCell>
                  <TableCell>{row.room}</TableCell>
                  <TableCell className="text-center">
                    {row.qrCodeUrl?.trim() && (
                      <img
                        src={row.qrCodeUrl}
                        alt={`QR for Room ${row.room}`}
                        className="w-10 h-10 mx-auto rounded border bg-white p-0.5"
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add Floor and Rooms Modal - Modern shadcn UI Design */}
      <Dialog
        open={showForm}
        onOpenChange={(open) => !open && handleCloseModal()}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader className="pb-4 border-b">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <BuildingIcon className="h-6 w-6 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-2xl font-bold">
                  Add Floor and Rooms
                </DialogTitle>
                <DialogDescription className="text-sm text-muted-foreground mt-1">
                  Add floors and rooms to a building
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 px-1">
            <div className="grid gap-6 py-6">
              {/* Select Building */}
              <div className="grid gap-2">
                <Label
                  htmlFor="building"
                  className="text-sm font-medium flex items-center gap-2 mb-1"
                >
                  <BuildingIcon className="h-3.5 w-3.5 text-muted-foreground" />
                  Select Building <span className="text-red-500">*</span>
                </Label>
                <ReactSelect
                  isSearchable
                  placeholder="Search and select building..."
                  options={assetOptions}
                  value={assetOptions.find(
                    (opt) => opt.value === selectedAssetCode,
                  )}
                  onChange={(option) => {
                    setSelectedAssetCode(option?.value || "");
                    setSelectedAssetTitle(option?.label || "");
                    setFloorAndRooms(option?.existingFloorRooms || {});
                  }}
                  styles={{
                    control: (base) => ({
                      ...base,
                      backgroundColor: "transparent",
                      borderColor: "hsl(var(--input))",
                      minHeight: "40px",
                      borderRadius: "calc(var(--radius) - 2px)",
                      fontSize: "14px",
                    }),
                    option: (base, state) => ({
                      ...base,
                      backgroundColor: state.isSelected
                        ? "hsl(var(--primary))"
                        : state.isFocused
                          ? "hsl(var(--accent))"
                          : "transparent",
                      color: state.isSelected
                        ? "hsl(var(--primary-foreground))"
                        : "hsl(var(--foreground))",
                      fontSize: "14px",
                    }),
                    menu: (base) => ({
                      ...base,
                      backgroundColor: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "calc(var(--radius) - 2px)",
                      zIndex: 9999,
                    }),
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    singleValue: (base) => ({
                      ...base,
                      color: "hsl(var(--foreground))",
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "hsl(var(--muted-foreground))",
                    }),
                    input: (base) => ({
                      ...base,
                      color: "hsl(var(--foreground))",
                    }),
                  }}
                />
              </div>

              {/* Floor Name */}
              <div className="grid gap-2">
                <Label
                  htmlFor="floor"
                  className="text-sm font-medium flex items-center gap-2"
                >
                  <Home className="h-3.5 w-3.5 text-muted-foreground" />
                  Floor Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="floor"
                  type="text"
                  placeholder="Enter floor name (e.g., Ground Floor)"
                  value={floorInput}
                  onChange={(e) => setFloorInput(e.target.value)}
                />
              </div>

              {/* Room Name with Add Button */}
              <div className="grid gap-2">
                <Label
                  htmlFor="room"
                  className="text-sm font-medium flex items-center gap-2"
                >
                  <DoorClosed className="h-3.5 w-3.5 text-muted-foreground" />
                  Room Name <span className="text-red-500">*</span>
                </Label>
                <div className="flex gap-2">
                  <Input
                    id="room"
                    type="text"
                    placeholder="Enter room name (e.g., Room 101)"
                    value={roomInput}
                    onChange={(e) => setRoomInput(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    onClick={addRoom}
                    variant="secondary"
                    className="gap-2"
                    style={{
                      backgroundColor:
                        "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                      color: "white",
                    }}
                  >
                    <Plus className="h-4 w-4" />
                    Add Room
                  </Button>
                </div>
              </div>

              {/* Current Floor and Rooms Display */}
              <div className="grid gap-2">
                <Label className="text-sm font-medium text-primary">
                  Current Floors and Rooms
                </Label>
                <div className="border rounded-lg p-4 bg-muted/50 min-h-[120px]">
                  {Object.entries(floorAndRooms).length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No floors or rooms added yet. Add a floor and room above.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(floorAndRooms).map(([floor, rooms]) => (
                        <div
                          key={floor}
                          className="bg-background rounded-md p-3 border"
                        >
                          <div className="flex items-start gap-2">
                            <Home className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                            <div className="flex-1">
                              <p className="font-medium text-sm">{floor}</p>
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {rooms.map((room, idx) => (
                                  <Badge
                                    key={idx}
                                    variant="secondary"
                                    className="text-xs"
                                  >
                                    <DoorClosed className="h-3 w-3 mr-1" />
                                    {room}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="pt-4 border-t">
            <div className="flex items-center gap-3 w-full sm:w-auto">
              <Button
                type="button"
                variant="outline"
                onClick={handleCloseModal}
                disabled={isLoading}
                className="flex-1 sm:flex-none"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleAddFloorRooms}
                disabled={isLoading}
                className="flex-1 sm:flex-none"
                style={{
                  backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
                }}
              >
                {isLoading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-background border-t-transparent mr-2" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <BuildingIcon className="mr-2 h-4 w-4" />
                    Submit
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      <DataTablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={allRows.length}
        itemsPerPage={rowsPerPage}
        onPageChange={setCurrentPage}
      />

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <BuildingIcon className="h-5 w-5 text-blue-600" />
              Confirm Addition
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-2">
              You are about to add floors/rooms to:{" "}
              <span className="font-semibold text-foreground">
                {selectedAssetTitle}
              </span>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleConfirmAddFloorRooms}
              disabled={isLoading}
              style={{
                backgroundColor: "rgb(48 88 69 / var(--tw-bg-opacity, 1))",
              }}
            >
              {isLoading ? "Adding..." : "Yes, Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Deletion Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2">
              <FaTrashAlt className="h-5 w-5 text-red-600" />
              Confirm Deletion
            </DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-2">
              Are you sure you want to delete{" "}
              <span className="font-semibold text-foreground">
                {selectedRows.length}
              </span>{" "}
              selected room(s)? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={confirmBulkDelete}
              disabled={isDeleting}
              variant="destructive"
            >
              {isDeleting ? "Deleting..." : "Yes, Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RoomQR;
